// src/validation/validationSchema.js
import * as yup from "yup";
import i18next from "i18next";

// Common email and password schema
const emailSchema = yup
  .string()
  .transform((value) => value?.toLowerCase() || "")
  .required(() => i18next.t("mediationRequest.validation.emailRequired"))
  .matches(
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    () => i18next.t("mediationRequest.validation.validEmail"),
  );

// Common PhoneNumber  schema
const phoneNumberSchema = yup
  .string()
  .matches(/^\d{10,15}$/, i18next.t("mediationRequest.validation.phoneDigits"))
  .required(i18next.t("mediationRequest.validation.phoneRequired"));

// Common password schema
const passwordSchema = yup
  .string()
  .required("Password is required.")
  .min(8, "Password must be at least 8 characters.")
  .matches(/[a-z]/, "At least one lowercase letter is required.")
  .matches(/[A-Z]/, "At least one uppercase letter is required.")
  .matches(/\d/, "At least one number is required.")
  .matches(/[@$!%*?&]/, "At least one special character is required.");

// Common validation schema for profile image
const profileImageSchema = yup
  .mixed()
  .required("Profile picture is required")
  .test("fileType", "Unsupported File Format", (value) => {
    return (
      value && ["image/jpeg", "image/png", "image/jpg"].includes(value.type)
    );
  });
// Common validation schema for name
const nameSchema = yup
  .string()
  .required("Name is required")
  .min(3, "Name is too short, minimum 3 characters")
  .max(30, "Name is too long")
  .matches(/^[a-zA-Z ]*$/, "Name should only contain alphabets");

// Common validation schema for date
const dateSchema = yup.date().required("Date is Required");

// reCaptcha validation schema
// const reCaptchaSchema = yup.string().required("ReCaptcha field is required");

// Define userType-specific schemas
const userTypeDependentSchemas = {
  generalPublic: yup.object().shape({
    fullName: yup.string().required("Full Name is required"),
  }),
  governmentEmployee: yup.object().shape({
    governmentID: yup.string().required("Government ID is required"),
  }),
  student: yup.object().shape({
    studentID: yup.string().required("Student ID is required"),
  }),
  privateSectorWithMembership: yup.object().shape({
    businessLicense: yup.string().required("Business license is required"),
    membershipNumber: yup.string().required("Membership number is required"),
  }),
  privateSectorWithoutMembership: yup.object().shape({
    businessLicense: yup.string().required("Business license is required"),
  }),
  arbitrator: yup.object().shape({
    arbitratorCategory: yup
      .string()
      .required("Arbitrator Category is required"),
    certifications: yup
      .array()
      .of(yup.mixed().required("Certification file is required"))
      .min(1, "At least one certification is required")
      .required("Certifications are required"),
    experience: yup.string().required("Experience is required"),
  }),
  expert: yup.object().shape({
    expertiseField: yup.string().required("Expertise field is required"),
  }),
  translator: yup.object().shape({
    languages: yup
      .array()
      .of(yup.string().required("Language is required"))
      .min(1, "At least one language is required")
      .required("Languages are required"),
    certifications: yup
      .array()
      .of(yup.mixed().required("Certification file is required"))
      .min(1, "At least one certification is required")
      .required("Certifications are required"),
    experience: yup.string().required("Experience is required"),
    translationRate: yup
      .number()
      .positive()
      .required("Translation rate is required"),
  }),
  systemAdministrator: yup.object().shape({
    adminID: yup.string().required("Administrator ID is required"),
  }),

  centerAdministrator: yup.object().shape({
    adminID: yup.string().required("Center Administrator ID is required"),
  }),

  centerEmployee: yup.object().shape({
    employeeID: yup.string().required("Employee ID is required"),
  }),
};

export const registerSchema = yup;
//   .object()
//   .shape({
//     userName: nameSchema,
//     email: emailSchema,
//     password: passwordSchema,
//     confirmPassword: yup
//       .string()
//       .oneOf([yup.ref("password")], "Passwords do not match")
//       .required("Confirm Password is required"),
//     profileImage: profileImageSchema,
//     arbitratorCheck: yup.boolean(),
//     userType: yup
//       .string()
//       .oneOf(Object.keys(userTypeDependentSchemas), "Invalid user type")
//       .required("User type is required"),
//     reCaptcha: reCaptchaSchema,
//   })
//   .test(
//     "userType-dependent-validation",
//     "Invalid user type details",
//     function (values, context) {
//       const { userType, ...rest } = values;

//       if (userType && userTypeDependentSchemas[userType]) {
//         try {
//           userTypeDependentSchemas[userType].validateSync(rest, {
//             context: this.options.context,
//             abortEarly: false,
//           });
//           return true;
//         } catch (err) {
//           if (err.inner && err.inner.length) {
//             const errors = err.inner.map((validationError) =>
//               this.createError({
//                 path: validationError.path,
//                 message: validationError.message,
//               }),
//             );
//             throw new yup.ValidationError(errors);
//           }
//         }
//       }
//       return true;
//     },
//   );
// Schema for profile update
export const profileSchema = yup.object().shape({
  username: nameSchema,
  profileImage: profileImageSchema,
});

// Schema for user profile details update
export const profileDetailsSchema = yup.object().shape({
  fullName: yup
    .string()
    .required(() => i18next.t("profile.validation.nameRequired"))
    .min(2, () => i18next.t("profile.validation.nameTooShort"))
    .max(100, () => i18next.t("profile.validation.nameTooLong")),
  userName: yup
    .string()
    .required(() => i18next.t("profile.validation.userNameRequired"))
    .min(3, () => i18next.t("profile.validation.userNameTooShort"))
    .max(30, () => i18next.t("profile.validation.userNameTooLong"))
    .matches(/^[a-zA-Z0-9_]+$/, () =>
      i18next.t("profile.validation.userNamePattern"),
    ),
  email: emailSchema,
  phone: phoneNumberSchema,
});

// Schema for login or authentication
export const loginSchema = yup.object().shape({
  email: emailSchema,
  password: passwordSchema,
  // reCaptcha: reCaptchaSchema,
});

// Schema for forgot password
export const forgotPasswordSchema = yup.object().shape({
  email: emailSchema,
});
// Schema for change password
export const changePasswordSchema = yup.object().shape({
  password: passwordSchema,
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password")], "Passwords do not match")
    .required("Confirm Password is required"),
});

// Schema for resend  email
export const resendEmailSchema = yup.object().shape({
  email: emailSchema,
});

// Schema for FAQ
export const faqSchema = yup.object().shape({
  name: nameSchema,
  email: emailSchema,
  subject: yup.string().required("Subject is required"),
  message: yup.string().required("Message is required"),
});

// Define the schema for an arbitration claimant
export const arbitrationSchema = yup.object().shape({
  arbitrationClaimant: yup
    .array()
    .of(
      yup.object().shape({
        name: nameSchema,
        address: yup.string().required("Address is required"),
        workTelephone: yup.string().nullable(), // Not required
        fax: yup.string().nullable(), // Not required
        lawyer: yup.string().nullable(), // Not required
        lawyerAddress: yup.string().required("Lawyer Address is required"),
        phoneNumber: phoneNumberSchema,
        email: emailSchema,
      }),
    )
    .required("At least one claimant is required"),

  arbitrationRespondent: yup
    .array()
    .of(
      yup.object().shape({
        name: nameSchema,
        address: yup.string().required("Address is required"),
        workTelephone: yup.string().nullable(), // Not required
        fax: yup.string().nullable(), // Not required
        phoneNumber: phoneNumberSchema,
        email: emailSchema,
      }),
    )
    .required("At least one respondent is required"),

  subject: yup.string().required("Subject is required"),

  claims: yup.string().required("Claims are required"),

  documents: yup.array().of(yup.mixed().required("A file is required")),
});

// Define the schema for an Intervention claimant
export const interventionSchema = yup.object().shape({
  interventionClaimant: yup
    .array()
    .of(
      yup.object().shape({
        name: nameSchema,
        address: yup.string().required("Address is required"),
        workTelephone: yup.string().nullable(), // Not required
        fax: yup.string().nullable(), // Not required
        lawyer: yup.string().nullable(), // Not required
        lawyerAddress: yup.string().required("Lawyer Address is required"),
        phoneNumber: phoneNumberSchema,
        email: emailSchema,
      }),
    )
    .required("At least one claimant is required"),

  interventionRespondent: yup
    .array()
    .of(
      yup.object().shape({
        name: nameSchema,
        address: yup.string().required("Address is required"),
        workTelephone: yup.string().nullable(), // Not required
        fax: yup.string().nullable(), // Not required
        phoneNumber: phoneNumberSchema,
        email: emailSchema,
      }),
    )
    .required("At least one respondent is required"),

  subject: yup.string().required("Subject is required"),

  claims: yup.string().required("Claims are required"),

  documents: yup.array().of(yup.mixed().required("A file is required")),
});

// Define the schema for an Joinder claimant
export const joinderSchema = yup.object().shape({
  joinderClaimant: yup
    .array()
    .of(
      yup.object().shape({
        name: nameSchema,
        address: yup.string().required("Address is required"),
        workTelephone: yup.string().nullable(), // Not required
        fax: yup.string().nullable(), // Not required
        lawyer: yup.string().nullable(), // Not required
        lawyerAddress: yup.string().required("Lawyer Address is required"),
        phoneNumber: phoneNumberSchema,
        email: emailSchema,
      }),
    )
    .required("At least one claimant is required"),

  joinderRespondent: yup
    .array()
    .of(
      yup.object().shape({
        name: nameSchema,
        address: yup.string().required("Address is required"),
        workTelephone: yup.string().nullable(), // Not required
        fax: yup.string().nullable(), // Not required
        phoneNumber: phoneNumberSchema,
        email: emailSchema,
      }),
    )
    .required("At least one respondent is required"),

  subject: yup.string().required("Subject is required"),

  claims: yup.string().required("Claims are required"),

  documents: yup.array().of(yup.mixed().required("A file is required")),
});

export const certifiedCopySchema = yup.object().shape({
  caseDate: yup.string().required("Date is Required"),
  caseNo: yup.string().required("Case No. is Required"),
  legalRepresentative: yup
    .string()
    .required("Legal Representative is Required")
    .matches(/^[A-Za-z\s]+$/, "Invalid Name"),
  email: emailSchema,
  phoneNumber: phoneNumberSchema,
  copies: yup
    .string()
    .required("No of copies is Required")
    .matches(/^\d+$/, "Phone number must contain only numbers"),
  signatureDate: yup
    .date()
    .required("Date of issue is Required")
    .min(new Date(), "Signature date must be today or in the future"),
  signature: yup.string().required("Signature is Required"),
});

export const interpretationSchema = yup.object().shape({
  caseDate: yup.date().required("Date is required"),
  caseNo: yup.string().required("Case No. is required"),
  legalRepresentative: yup
    .string()
    .required("Legal Representative is required")
    .matches(/^[a-zA-Z\s]*$/, "Only letters and spaces are allowed"),
  email: emailSchema,
  phoneNumber: phoneNumberSchema,
  awardDateOfIssuance: yup
    .date()
    .required("Award Date of Issuance is required"),
  awardDateOfReceipt: yup.date().required("Award Date of Receipt is required"),
  interpretationSubject: yup
    .string()
    .required("Interpretation Subject is required"),
  signatureDate: yup.date().required("Signature Date is required"),
  signature: yup.string().required("Signature is required"),
  documents: yup.mixed().required("A file is required"),
  // reCaptcha: reCaptchaSchema,
});

const contactEmailRegex =
  /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

/** Rejects emoji / pictographic characters in the email string */
const contactEmailEmojiRegex = /\p{Extended_Pictographic}/u;

const contactEmailHasLetterBeforeAt = (value) => {
  const localPart = value?.split("@")[0] ?? "";
  return /[a-zA-Z]/.test(localPart);
};

/**
 * Contact form schema with i18n messages. Pass `t` from useTranslation().
 * Recreate the resolver when language changes (e.g. useMemo(..., [t, i18n.language])).
 */
export const getContactSchema = (t) =>
  yup.object().shape({
    name: yup
      .string()
      .required(t("contact.validation.nameRequired"))
      .min(3, t("contact.validation.nameMin"))
      .max(30, t("contact.validation.nameMax"))
      .matches(/^[a-zA-Z ]*$/, t("contact.validation.nameLettersOnly")),
    email: yup
      .string()
      .transform((value) => value?.toLowerCase() || "")
      .required(t("contact.validation.emailRequired"))
      .matches(contactEmailRegex, t("contact.validation.emailInvalid"))
      .test(
        "contact-email-letter-before-at",
        t("contact.validation.emailLetterBeforeAt"),
        (value) => !value || contactEmailHasLetterBeforeAt(value),
      )
      .test(
        "contact-email-no-emoji",
        t("contact.validation.emailNoEmoji"),
        (value) => !value || !contactEmailEmojiRegex.test(value),
      ),
    phoneNumber: yup
      .string()
      .required(t("contact.validation.phoneRequired"))
      .test(
        "contact-phone-digits",
        t("contact.validation.phoneDigits"),
        (value) => {
          if (!value) return false;
          const digits = value.replace(/\D/g, "");
          return digits.length >= 8 && digits.length <= 15;
        },
      ),
    subject: yup.string().required(t("contact.validation.subjectRequired")),
    message: yup
      .string()
      .required(t("contact.validation.messageRequired"))
      .min(10, t("contact.validation.messageMin")),
    enquiryType: yup
      .string()
      .oneOf(
        ["personal", "company"],
        t("contact.validation.enquiryTypeInvalid"),
      )
      .required(t("contact.validation.enquiryTypeRequired")),
    locationType: yup
      .string()
      .oneOf(
        ["internal", "external"],
        t("contact.validation.locationTypeInvalid"),
      )
      .required(t("contact.validation.locationTypeRequired")),
    emirate: yup.mixed().when("locationType", {
      is: "internal",
      then: (schema) => schema.required(t("contact.selectEmirateRequired")),
      otherwise: (schema) => schema.nullable(),
    }),
    country: yup.mixed().when("locationType", {
      is: "external",
      then: (schema) => schema.required(t("contact.selectCountryRequired")),
      otherwise: (schema) => schema.nullable(),
    }),
  });

// Define claim validation schema
export const claimSchema = yup.object().shape({
  caseDate: dateSchema,
  caseNo: yup.string().required("Case No. is required"),
  legalRepresentative: yup
    .string()
    .required("Legal Representative is required")
    .matches(/^[a-zA-Z ]*$/, "Only alphabets are allowed"),
  email: emailSchema,
  phoneNumber: phoneNumberSchema,
  copies: yup
    .number()
    .required("Number of copies is required")
    .min(1, "At least one copy is required"),
  signatureDate: yup.date().required("Signature Date is required"),
  signature: yup.string().required("Signature is required"),
  // reCaptcha: reCaptchaSchema,
});

// Define objection validation schema
export const objectionSchema = yup.object().shape({
  caseDate: dateSchema,
  caseNo: yup.string().required("Case No. is required"),
  legalRepresentative: yup
    .string()
    .required("Legal Representative is required")
    .matches(/^[a-zA-Z ]*$/, "Only alphabets are allowed"),
  email: emailSchema,
  phoneNumber: phoneNumberSchema,
  challenge: yup.string().required("Challenge is required"),
  signatureDate: yup.date().required("Signature Date is required"),
  signature: yup.string().required("Signature is required"),
});

// Define certificate  validation schema
export const certificateSchema = yup.object().shape({
  caseDate: dateSchema,
  RequestedBy: yup.string().required("Requested by is Required"),
  email: emailSchema,
  phoneNumber: phoneNumberSchema,
  AddressedTo: yup.string().required("Addressed To field is Required"),
  otherDetails: yup.string().required("Further details is Required"),
  signatureDate: yup.date().required("Date of issue is Required"),
  signature: yup.string().required("Signature is Required"),
  selectedSubject: yup
    .string()
    .required("Please select a subject of the certificate"),
});

// Define validation schema
export const nominationSchema = yup.object({
  caseDate: dateSchema,
  caseNo: yup.string().required("Case number is required"),
  RequestedBy: yup.string().required("Requested by is required"),
  email: emailSchema,
  phoneNumber: phoneNumberSchema,
  nameOfArbitrator: nameSchema,
  nationality: yup.object().required("Nationality is required"),
  specialization: yup.string().required("Specialization is required"),
  addressAritrator: yup.string().required("Address is required"),
  emailArbitrator: emailSchema,
  phoneNumberArbitrator: phoneNumberSchema,
  challenge: yup.string().required("Challenge is required"),
  signatureDate: yup.date().required("Signature date is required"),
  signature: yup.string().required("Signature is required"),
});

// Define not governed validation schema
export const nonGovernedSchema = yup.object().shape({
  caseDate: dateSchema,
  caseNo: yup.string().required("Case No. is required"),
  legalRepresentative: yup
    .string()
    .required("Legal Representative is required")
    .matches(/^[a-zA-Z ]*$/, "Only alphabets are allowed"),
  email: emailSchema,
  phoneNumber: phoneNumberSchema,
  applicableLaw: yup.string().required("applicable law is required"),
  signatureDate: yup.date().required("Signature Date is required"),
  signature: yup.string().required("Signature is required"),
});

export const pollOptionsSchema = yup.object().shape({
  selectedOption: yup.string().required("You must select an option"),
});

export const feedbackSchema = yup.object().shape({
  feedback: yup.string().optional(),
  rating: yup
    .number()
    .min(1, "Rating must be at least 1")
    .max(5, "Rating must be at most 5")
    .required("Rating is required"),
});

export const reportSchema = yup.object().shape({
  pageUrl: yup.string().required("URL is required"),
  feedback: yup.string().required("Comment is required"),
});

export const mediationReqSchema = yup.object().shape({
  claimantName: yup
    .string()
    .required("Claimant name is required")
    .min(1, "Claimant name must be at least 1 character long"),
  claimantNationality: yup
    .string()
    .required("Claimant nationality is required")
    .min(1, "Claimant nationality must be at least 1 character long"),
  additionalClaimantsNames: yup.string().optional(),
  additionalClaimantsNationalities: yup.string().optional(),
  idNumber: yup.string().optional(),
  addressLine1: yup
    .string()
    .required("Address line 1 is required")
    .min(1, "Address line 1 must be at least 1 character long"),
  addressLine2: yup.string().optional(),
  addressLine3: yup.string().optional(),
  poBox: yup.string().optional(),
  city: yup
    .string()
    .required("City is required")
    .min(1, "City must be at least 1 character long"),
  postCode: yup.string().optional(),
  stateProvince: yup.string().optional(),
  country: yup
    .string()
    .required("Country is required")
    .min(1, "Country must be at least 1 character long"),
  nationalAddress: yup.string().optional(),
  telephoneNumber: yup
    .string()
    .required("Telephone number is required")
    .min(1, "Telephone number must be at least 1 character long"),
  emailAddress: yup
    .string()
    .required("Email address is required")
    .email("Email address must be a valid email"),
  representativeName: yup
    .string()
    .required("Representative name is required")
    .min(1, "Representative name must be at least 1 character long"),
  representativeFirmName: yup.string().optional(),
  representativeAddressLine1: yup
    .string()
    .required("Representative address line 1 is required")
    .min(1, "Representative address line 1 must be at least 1 character long"),
  representativeAddressLine2: yup.string().optional(),
  representativeAddressLine3: yup.string().optional(),
  representativePOBox: yup.string().optional(),
  representativeCity: yup
    .string()
    .required("Representative city is required")
    .min(1, "Representative city must be at least 1 character long"),
  representativePostCode: yup.string().optional(),
  representativeStateProvince: yup.string().optional(),
  representativeCountry: yup
    .string()
    .required("Representative country is required")
    .min(1, "Representative country must be at least 1 character long"),
  representativeNationalAddress: yup.string().optional(),
  representativeTelephoneNumber: yup
    .string()
    .required("Representative telephone number is required")
    .min(
      1,
      "Representative telephone number must be at least 1 character long",
    ),
  representativeMobileNumber: yup
    .string()
    .required("Representative mobile number is required")
    .min(1, "Representative mobile number must be at least 1 character long"),
  representativeEmailAddress: yup
    .string()
    .required("Representative email address is required")
    .email("Representative email address must be a valid email"),
  dateOfContractOrMediationAgreement: yup
    .date()
    .required("Date of contract or mediation agreement is required")
    .typeError("Date must be a valid date"),
  textOfMediationClauseAgreement: yup
    .string()
    .required("Text of mediation clause agreement is required")
    .min(
      1,
      "Text of mediation clause agreement must be at least 1 character long",
    ),
  natureOfDispute: yup
    .string()
    .required("Nature of dispute is required")
    .min(1, "Nature of dispute must be at least 1 character long"),
  claimOrReliefSought: yup
    .string()
    .required("Claim or relief sought is required")
    .min(1, "Claim or relief sought must be at least 1 character long"),
  claimantTypeOfBusiness: yup
    .string()
    .required("Claimant type of business is required")
    .min(1, "Claimant type of business must be at least 1 character long"),
  respondentTypeOfBusiness: yup
    .string()
    .required("Respondent type of business is required")
    .min(1, "Respondent type of business must be at least 1 character long")
    .max(50, "Number of arbitrators must not exceed 50"),
  numberOfArbitrators: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : Number(originalValue),
    )
    .positive("Must be greater than zero")
    .integer("Must be an integer")
    .required("Number of arbitrators is required"),
  placeOfMediation: yup.string().optional(),
  language: yup.string().optional(),
  requestedMediatorQualifications: yup.string().optional(),
  respondentName: yup
    .string()
    .required("Respondent name is required")
    .min(1, "Respondent name must be at least 1 character long"),
  respondentNationality: yup
    .string()
    .required("Respondent nationality is required")
    .min(1, "Respondent nationality must be at least 1 character long"),
  additionalRespondentsNames: yup.string().optional(),
  additionalRespondentsNationalities: yup.string().optional(),
  respondentIdNumber: yup.string().optional(),
  respondentAddress: yup
    .string()
    .required("Respondent address is required")
    .min(1, "Respondent address must be at least 1 character long"),
  respondentPOBox: yup.string().optional(),
  respondentCity: yup
    .string()
    .required("Respondent city is required")
    .min(1, "Respondent city must be at least 1 character long"),
  respondentPostCode: yup.string().optional(),
  respondentStateProvince: yup.string().optional(),
  respondentCountry: yup
    .string()
    .required("Respondent country is required")
    .min(1, "Respondent country must be at least 1 character long"),
  respondentNationalAddress: yup.string().optional(),
  respondentTelephoneNumber: yup
    .string()
    .required("Respondent telephone number is required")
    .min(1, "Respondent telephone number must be at least 1 character long"),
  respondentEmailAddress: yup
    .string()
    .required("Respondent email address is required")
    .email("Respondent email address must be a valid email"),
  acknowledgement1: yup.boolean().oneOf([true], "You must agree to continue."),
  acknowledgement2: yup.boolean().oneOf([true], "You must agree to continue."),
  acknowledgement3: yup.boolean().oneOf([true], "You must agree to continue."),
  acknowledgement4: yup.boolean().oneOf([true], "You must agree to continue."),
  acknowledgement5: yup.boolean().oneOf([true], "You must agree to continue."),
  acknowledgement6: yup.boolean().oneOf([true], "You must agree to continue."),
  acknowledgement7: yup.boolean().oneOf([true], "You must agree to continue."),
  acknowledgement8: yup.boolean().oneOf([true], "You must agree to continue."),
  acknowledgement9: yup.boolean().oneOf([true], "You must agree to continue."),
  documentUpload: yup
    .mixed()
    .test(
      "required",
      "A file upload is required to submit this form",
      (value) => {
        return value && value.length > 0;
      },
    ),
  confirmContractUpload: yup
    .boolean()
    .required("Contract upload confirmation is required")
    .default(false),
  confirmPowerOfAttorneyUpload: yup
    .boolean()
    .required("Power of attorney upload confirmation is required")
    .default(false),
  sendCopy: yup.boolean().optional(),
  email: yup
    .string()
    .email("Must be a valid email")
    .when("sendCopy", {
      is: true,
      then: (schema) =>
        schema.required("Email address is required when sending a copy."),
      otherwise: (schema) => schema.notRequired(),
    }),
});

export const arbitrationFormSchema = yup.object().shape({
  // Claimant Details
  claimantName: yup
    .string()
    .required(i18next.t("arbitrationForm.validation.claimantNameRequired")),
  claimantNationality: yup
    .string()
    .required(
      i18next.t("arbitrationForm.validation.claimantNationalityRequired"),
    ),
  additionalClaimants: yup.string().nullable(),
  additionalClaimantsNationalities: yup.string().nullable(),
  claimantIdOrRegNumber: yup
    .string()
    .required(
      i18next.t("arbitrationForm.validation.claimantIdOrRegNumberRequired"),
    ),
  claimantAddressLine1: yup
    .string()
    .required(
      i18next.t("arbitrationForm.validation.claimantAddressLine1Required"),
    ),
  claimantAddressLine2: yup.string().nullable(),
  claimantAddressLine3: yup.string().nullable(),
  claimantPOBox: yup.string().nullable(),
  claimantCity: yup
    .string()
    .required(i18next.t("arbitrationForm.validation.cityRequired")),
  claimantPostCode: yup.string().nullable(),
  claimantState: yup.string().nullable(),
  claimantCountry: yup
    .string()
    .required(i18next.t("arbitrationForm.validation.countryRequired")),
  claimantNationalAddress: yup.string().nullable(),
  claimantTelephone: yup
    .string()
    .required(
      i18next.t("arbitrationForm.validation.claimantTelephoneRequired"),
    ),
  claimantEmail: yup
    .string()
    .email(i18next.t("arbitrationForm.validation.invalidEmail"))
    .required(i18next.t("arbitrationForm.validation.emailRequired")),
  claimantBusinessType: yup.string().nullable(),

  // Claimant Representative
  repName: yup
    .string()
    .required(
      i18next.t("arbitrationForm.validation.representativeNameRequired"),
    ),
  repFirmName: yup.string().nullable(),
  repAddressLine1: yup
    .string()
    .required(
      i18next.t("arbitrationForm.validation.representativeAddressRequired"),
    ),
  repAddressLine2: yup.string().nullable(),
  repAddressLine3: yup.string().nullable(),
  repPOBox: yup.string().nullable(),
  repCity: yup
    .string()
    .required(
      i18next.t("arbitrationForm.validation.representativeCityRequired"),
    ),
  repPostCode: yup.string().nullable(),
  repState: yup.string().nullable(),
  repCountry: yup
    .string()
    .required(
      i18next.t("arbitrationForm.validation.representativeCountryRequired"),
    ),
  repNationalAddress: yup.string().nullable(),
  repTelephone: yup
    .string()
    .required(
      i18next.t("arbitrationForm.validation.representativeTelephoneRequired"),
    ),
  repMobile: yup
    .string()
    .required(
      i18next.t("arbitrationForm.validation.representativeMobileRequired"),
    ),
  repEmail: yup
    .string()
    .required(
      i18next.t("arbitrationForm.validation.representativeEmailRequired"),
    )
    .email(i18next.t("arbitrationForm.validation.invalidEmail")),

  // Respondent Details
  respondentName: yup
    .string()
    .required(i18next.t("arbitrationForm.validation.respondentNameRequired")),
  respondentNationality: yup
    .string()
    .required(
      i18next.t("arbitrationForm.validation.respondentNationalityRequired"),
    ),
  additionalRespondents: yup.string().nullable(),
  additionalRespondentsNationalities: yup.string().nullable(),
  respondentIdOrRegNumber: yup
    .string()
    .required(
      i18next.t("arbitrationForm.validation.respondentIdOrRegNumberRequired"),
    ),
  respondentAddress: yup
    .string()
    .required(
      i18next.t("arbitrationForm.validation.respondentAddressRequired"),
    ),
  respondentPOBox: yup.string().nullable(),
  respondentCity: yup
    .string()
    .required(i18next.t("arbitrationForm.validation.cityRequired")),
  respondentPostCode: yup.string().nullable(),
  respondentState: yup.string().nullable(),
  respondentCountry: yup
    .string()
    .required(i18next.t("arbitrationForm.validation.countryRequired")),
  respondentNationalAddress: yup.string().nullable(),
  respondentTelephone: yup
    .string()
    .required(i18next.t("arbitrationForm.validation.telephoneRequired")),
  respondentEmail: yup
    .string()
    .email(i18next.t("arbitrationForm.validation.invalidEmail"))
    .required(i18next.t("arbitrationForm.validation.emailRequired")),
  respondentBusinessType: yup.string().nullable(),

  // Respondent Representative
  respondentRepName: yup
    .string()
    .required(
      i18next.t("arbitrationForm.validation.representativeNameRequired"),
    ),
  respondentRepFirmName: yup.string().nullable(),
  respondentRepAddress: yup
    .string()
    .required(
      i18next.t("arbitrationForm.validation.representativeAddressRequired"),
    ),
  respondentRepPOBox: yup.string().nullable(),
  respondentRepCity: yup
    .string()
    .required(
      i18next.t("arbitrationForm.validation.representativeCityRequired"),
    ),
  respondentRepPostCode: yup.string().nullable(),
  respondentRepState: yup.string().nullable(),
  respondentRepCountry: yup
    .string()
    .required(
      i18next.t("arbitrationForm.validation.representativeCountryRequired"),
    ),
  respondentRepNationalAddress: yup.string().nullable(),
  respondentRepTelephone: yup
    .string()
    .required(i18next.t("arbitrationForm.validation.telephoneRequired")),
  respondentRepMobile: yup
    .string()
    .required(i18next.t("arbitrationForm.validation.mobileRequired")),
  respondentRepEmail: yup
    .string()
    .required(i18next.t("arbitrationForm.validation.emailRequired"))
    .email(i18next.t("arbitrationForm.validation.validEmailAddress")),

  // Dispute Details
  contractDate: yup
    .date()
    .transform((value, originalValue) =>
      originalValue === "" ? null : new Date(originalValue),
    )
    .required(i18next.t("arbitrationForm.validation.contractDateRequired")),
  arbitrationClauseText: yup
    .string()
    .required(
      i18next.t("arbitrationForm.validation.arbitrationClauseTextRequired"),
    ),
  natureOfDispute: yup
    .string()
    .required(i18next.t("arbitrationForm.validation.natureOfDisputeRequired")),
  claimReliefSought: yup
    .string()
    .required(
      i18next.t("arbitrationForm.validation.claimReliefSoughtRequired"),
    ),
  numberOfArbitrators: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : Number(originalValue),
    )
    .positive(i18next.t("arbitrationForm.validation.mustBeGreaterThanZero"))
    .integer(i18next.t("arbitrationForm.validation.mustBeInteger"))
    .required(
      i18next.t("arbitrationForm.validation.numberOfArbitratorsRequired"),
    ),
  placeOfArbitration: yup.string().nullable(),
  language: yup.string().nullable(),
  expeditedProcedureDetails: yup.string().nullable(),
  emergencyApplicationFiled: yup
    .boolean()
    .transform((value, originalValue) =>
      originalValue === "" ? false : value,
    ),
  // Acknowledgements
  infoAccurate: yup
    .boolean()
    .required(i18next.t("arbitrationForm.validation.mustAgreeToContinue"))
    .oneOf([true], i18next.t("arbitrationForm.validation.mustAgreeToContinue")),
  filingRequirementsMet: yup
    .boolean()
    .required(i18next.t("arbitrationForm.validation.mustAgreeToContinue"))
    .oneOf([true], i18next.t("arbitrationForm.validation.mustAgreeToContinue")),
  filingFeePaid: yup
    .boolean()
    .required(i18next.t("arbitrationForm.validation.mustAgreeToContinue"))
    .oneOf([true], i18next.t("arbitrationForm.validation.mustAgreeToContinue")),
  agreeToEmailCommunication: yup
    .boolean()
    .required(i18next.t("arbitrationForm.validation.mustAgreeToContinue"))
    .oneOf([true], i18next.t("arbitrationForm.validation.mustAgreeToContinue")),
  agreeToSMSCommunication: yup
    .boolean()
    .required(i18next.t("arbitrationForm.validation.mustAgreeToContinue"))
    .oneOf([true], i18next.t("arbitrationForm.validation.mustAgreeToContinue")),
  agreeToTerms: yup
    .boolean()
    .required(i18next.t("arbitrationForm.validation.mustAgreeToContinue"))
    .oneOf([true], i18next.t("arbitrationForm.validation.mustAgreeToContinue")),
  powerOfAttorney: yup
    .boolean()
    .required(i18next.t("arbitrationForm.validation.mustAgreeToContinue"))
    .oneOf([true], i18next.t("arbitrationForm.validation.mustAgreeToContinue")),
  arbitrationRequirementsMet: yup
    .boolean()
    .required(i18next.t("arbitrationForm.validation.mustAgreeToContinue"))
    .default(false),
  expeditedProcedureConfirmed: yup
    .boolean()
    .required(i18next.t("arbitrationForm.validation.mustAgreeToContinue"))
    .default(false),
  marketingConsent: yup
    .boolean()
    .required(i18next.t("arbitrationForm.validation.mustAgreeToContinue"))
    .default(false),
  noticeSentToRespondent: yup
    .boolean()
    .required(i18next.t("arbitrationForm.validation.mustAgreeToContinue"))
    .oneOf([true], i18next.t("arbitrationForm.validation.mustAgreeToContinue"))
    .default(false),
  documents: yup
    .mixed()
    .test(
      "fileRequired",
      i18next.t("arbitrationForm.validation.atLeastOneDocumentRequired"),
      (files) => {
        return files && files.length > 0;
      },
    )
    .test(
      "fileType",
      i18next.t("arbitrationForm.validation.onlyPdfAllowed"),
      (files) => {
        if (!files || files.length === 0) return true;
        return Array.from(files).every(
          (file) => file.type === "application/pdf",
        );
      },
    )
    .test(
      "fileCount",
      i18next.t("arbitrationForm.validation.maxTenFiles"),
      (files) => {
        if (!files || files.length === 0) return true;
        return files.length <= 10;
      },
    )
    .test(
      "fileSize",
      i18next.t("arbitrationForm.validation.maxFileSize30mb"),
      (files) => {
        if (!files || files.length === 0) return true;
        return Array.from(files).every((file) => file.size <= 30 * 1024 * 1024);
      },
    ),
});

export const applyArbitratorFormSchema = yup.object().shape({
  // checks
  certifiedCIArb: yup.string().optional(),
  certifiedCIArbMember: yup.string().optional(),
  atleastFiveCases: yup.string().optional(),
  oneCaseRecognized: yup.string().optional(),
  atleastFiveYOE: yup.string().optional(),
  atleastThreeCases: yup.string().optional(),

  // Personal Info
  prefix: yup.string().required("This field is required"),
  firstName: yup.string().required("This field is required"),
  lastName: yup.string().required("This field is required"),
  middleName: yup.string().optional(),
  nationalities: yup
    .array()
    .of(yup.string())
    .min(1, "At least one nationality is required")
    .required("This field is required"),
  languages: yup
    .array()
    .of(yup.string())
    .min(1, "At least one language is required")
    .required("This field is required"),
  location: yup.string().required("This field is required"),
  mobileNumber: yup.string().required("This field is required"),
  email: yup
    .string()
    .email("Enter a valid email")
    .required("This field is required"),

  // Arbitrator Info
  generalYOE: yup
    .number()
    .typeError("Must be a number")
    .min(0, "Must be at least 0")
    .required("This field is required"),
  arbitratorYOE: yup
    .number()
    .typeError("Must be a number")
    .min(0, "Must be at least 0")
    .required("This field is required"),
  numberOfAppointments: yup
    .number()
    .typeError("Must be a number")
    .min(0, "Must be at least 0")
    .required("This field is required"),
  appointmentsInLastThreeYears: yup
    .number()
    .typeError("Must be a number")
    .min(0, "Must be at least 0")
    .required("This field is required"),
  appointmentsAsChairOfTribunal: yup
    .number()
    .typeError("Must be a number")
    .min(0, "Must be at least 0")
    .required("This field is required"),
  currentEmployer: yup.string().required("This field is required"),
  areaOfExpertise: yup
    .array()
    .of(yup.string())
    .min(1, "Please select at least one area of expertise")
    .required("This field is required"),

  expertiseInADR: yup.string().required("This field is required"),

  // Acknowledgements (Checkboxes)
  ack1: yup
    .boolean()
    .oneOf([true], "Please check this to continue.")
    .required(),
  ack2: yup
    .boolean()
    .oneOf([true], "Please check this to continue.")
    .required(),
  ack3: yup
    .boolean()
    .oneOf([true], "Please check this to continue.")
    .required(),
  sendCopy: yup.boolean().optional().default(false),

  documents: yup
    .mixed()
    .test("fileRequired", "At least three documents are required", (files) => {
      return files && files.length > 2;
    })
    .test("fileType", "Only PDF, DOC, and DOCX files are allowed", (files) => {
      if (!files || files.length === 0) return true;
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      return Array.from(files).every((file) =>
        allowedTypes.includes(file.type),
      );
    })
    .test("fileCount", "You can upload a maximum of 6 files", (files) => {
      if (!files || files.length === 0) return true;
      return files.length <= 6;
    })
    .test("fileSize", "Each file must be 30MB or smaller", (files) => {
      if (!files || files.length === 0) return true;
      return Array.from(files).every((file) => file.size <= 30 * 1024 * 1024);
    }),
});

export const mediationRequestValidationSchema = yup.object({
  serviceRequestType: yup
    .string()
    .oneOf(["Mediation Services"])
    .default("Mediation Services"),
  serviceRequestSubType: yup.string().oneOf(["Mediation"]).default("Mediation"),
  memberNameEnu: yup
    .string()
    .min(3, i18next.t("mediationRequest.validation.memberNameEnglishMin"))
    .max(255, i18next.t("mediationRequest.validation.memberNameEnglishMax"))
    .required(
      i18next.t("mediationRequest.validation.memberNameEnglishRequired"),
    ),
  memberNameAra: yup.string().optional(),
  memberNumber: yup.string().optional(),
  dateOfSubmission: yup.date().default(() => new Date(), "current date"),
  paymentMethod: yup
    .string()
    .oneOf(["Online", "Bank Transfer", "Credit Card", "Cash", "Cheque"])
    .default("Online"),

  // Complainant Details
  complainantNameEnu: yup
    .string()
    .min(3, () =>
      i18next.t("mediationRequest.validation.complainantNameEnglishMin"),
    )
    .max(255, () =>
      i18next.t("mediationRequest.validation.complainantNameEnglishMax"),
    )
    .required(() =>
      i18next.t("mediationRequest.validation.complainantNameEnglishRequired"),
    ),
  complainantNameAra: yup.string().optional(),
  complainantAddressLine1: yup.string().optional(),
  complainantAddressLine2: yup.string().optional(),
  complainantMainEmailAddress: emailSchema,

  // Inquirer Details
  inquirerName: yup
    .string()
    .min(3)
    .max(255)
    .required(() =>
      i18next.t("mediationRequest.validation.inquirerNameRequired"),
    ),
  inquirerDesignation: yup
    .string()
    .required(() =>
      i18next.t("mediationRequest.validation.designationRequired"),
    ),
  inquirerMobile: phoneNumberSchema,
  inquirerTelephone: phoneNumberSchema,
  inquirerEmailId: emailSchema,
  inquirerPOBoxNumber: yup
    .string()
    .required(() => i18next.t("mediationRequest.validation.poBoxRequired")),
  inquirerCity: yup
    .string()
    .required(() => i18next.t("mediationRequest.validation.cityRequired")),
  inquirerCountry: yup
    .string()
    .required(() => i18next.t("mediationRequest.validation.countryRequired")),
  respondentName: yup
    .string()
    .min(3)
    .max(255)
    .required(() =>
      i18next.t("mediationRequest.validation.respondentNameRequired"),
    ),
  respondentCompanyNameAra: yup.string().optional(),
  isDubaiChamberMember: yup.boolean().default(false),
  membershipNo: yup.string().optional(),
  respondentAddressLine1: yup.string().optional(),
  respondentCountry: yup
    .string()
    .min(1)
    .max(255)
    .required(() =>
      i18next.t("mediationRequest.validation.respondentCountryRequired"),
    ),
  respondentCity: yup
    .string()
    .required(() => i18next.t("mediationRequest.validation.cityRequired")),
  respondentEmail: emailSchema,
  respondentTelephone: phoneNumberSchema,
  respondentMobile: yup.string().optional(),
  respondentAddressLine2: yup.string().optional(),

  // Dispute Details
  disputeInBrief: yup
    .string()
    .min(1)
    .max(1000)
    .required(() =>
      i18next.t("mediationRequest.validation.disputeDescriptionRequired"),
    ),
  complainantRequest: yup
    .string()
    .min(1)
    .max(1000)
    .required(() =>
      i18next.t("mediationRequest.validation.complainantRequestRequired"),
    ),
  disputeAmount: yup
    .number()
    .min(0, () =>
      i18next.t("mediationRequest.validation.disputeAmountPositive"),
    )
    .required(() =>
      i18next.t("mediationRequest.validation.disputeAmountRequired"),
    ),
  termsAccepted: yup
    .boolean()
    .required(() => i18next.t("mediationRequest.validation.acceptTerms")),

  // Document Management
  attachedDocuments: yup
    .array()
    .of(
      yup.object({
        file: yup
          .mixed()
          .required(() => i18next.t("mediationRequest.validation.fileRequired"))
          .test(
            "fileType",
            () => i18next.t("mediationRequest.validation.onlyPdfAllowed"),
            (value) => {
              return value && value.type === "application/pdf";
            },
          )
          .test(
            "fileSize",
            () => i18next.t("mediationRequest.validation.maxFileSize10mb"),
            (value) => {
              return value && value.size <= 10 * 1024 * 1024;
            },
          ),
        documentType: yup
          .string()
          .required(() =>
            i18next.t("mediationRequest.validation.documentTypeRequired"),
          ),
      }),
    )
    .min(1, () =>
      i18next.t("mediationRequest.validation.atLeastOneDocumentRequired"),
    )
    .required(() => i18next.t("mediationRequest.validation.documentsRequired"))
    .test(
      "fileCount",
      () => i18next.t("mediationRequest.validation.maxTenFiles"),
      (files) => {
        return files.length <= 10;
      },
    )
    .min(1, () =>
      i18next.t("mediationRequest.validation.atLeastOneDocumentUploaded"),
    )
    .required(() => i18next.t("mediationRequest.validation.documentsRequired")),

  documentType: yup
    .string()
    .required(() =>
      i18next.t("mediationRequest.validation.documentTypeRequired"),
    ),
  notes: yup.array().of(
    yup.object({
      noteText: yup.string().notRequired(),
    }),
  ),
});

export const registrationSchema = yup.object().shape({
  uaeResident: yup
    .string()
    .required("Please select if you are a UAE resident."),

  emiratesID: yup.string().when("uaeResident", {
    is: "yes",
    then: (schema) =>
      schema
        .required("Emirates ID is required.")
        .matches(
          /^[0-9]{15}$/,
          "Invalid ID. Please enter a valid 15 digit ID.",
        ),
    otherwise: (schema) => schema.notRequired(),
  }),

  firstName: yup
    .string()
    .required("First name is required.")
    .max(50, "First name must be at most 50 characters."),

  lastName: yup
    .string()
    .required("Last name is required.")
    .max(50, "Last name must be at most 50 characters."),

  userName: yup
    .string()
    .required("Username is required.")
    .min(3, "Username must be at least 3 characters.")
    .max(30, "Username must be at most 30 characters."),

  email: yup
    .string()
    .required("Email is required.")
    .email("Enter a valid email address."),

  password: passwordSchema,

  title: yup.string().optional(),
  nationality: yup.string().optional(),
  passport: yup.string().optional(),

  phone: yup
    .string()
    .required("Invalid number.")
    .min(6, "Too short.")
    .max(20, "Too long."),

  // reCaptcha: yup.string().required("Please complete the CAPTCHA verification."),
});
